import type {
  CoPilotAgentMessageDebugEntry,
  CoPilotAgentMessageToolCallDebug,
  CoPilotAgentMessagesDebugResponse,
  CoPilotSubAgentRunDebug,
} from '@terra/shared-dto-chat-types';
import { extractReadableText } from './agentMessageViewUtils';

export type AgentTraceNodeKind =
  | 'chain'
  | 'system'
  | 'human'
  | 'llm'
  | 'tool'
  | 'subagent';

const TASK_TOOL_NAME = 'task';

type TraceBuildContext = {
  subAgentRunsByToolCallId: Map<string, CoPilotSubAgentRunDebug>;
  visitedToolCallIds: Set<string>;
};

export type AgentTraceIO =
  | { type: 'text'; text: string }
  | { type: 'json'; value: unknown }
  | {
      type: 'llm';
      text: string;
      toolCalls: CoPilotAgentMessageToolCallDebug[];
    };

export type AgentTraceNode = {
  id: string;
  kind: AgentTraceNodeKind;
  name: string;
  preview: string;
  status?: string;
  skipped?: boolean;
  input?: AgentTraceIO;
  output?: AgentTraceIO;
  metadata: Record<string, unknown>;
  children: AgentTraceNode[];
};

export type AgentTraceTree = {
  root: AgentTraceNode;
  degraded: boolean;
};

const PREVIEW_MAX_CHARS = 140;

function toPreview(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, PREVIEW_MAX_CHARS);
}

function nodeName(entry: CoPilotAgentMessageDebugEntry): string {
  switch (entry.messageType) {
    case 'human':
      return 'Human';
    case 'ai':
      return 'AI';
    case 'system':
      return 'System';
    case 'tool':
      return entry.name ?? 'Tool';
    default:
      return 'Message';
  }
}

function isMatch(
  entry: CoPilotAgentMessageDebugEntry,
  toolCall: CoPilotAgentMessageToolCallDebug,
): boolean {
  if (toolCall.id || entry.toolCallId) {
    return Boolean(
      toolCall.id && entry.toolCallId && entry.toolCallId === toolCall.id,
    );
  }

  if (toolCall.name && entry.name) {
    return entry.name === toolCall.name;
  }

  return false;
}

function findToolEntryIndex({
  toolCall,
  toolEntries,
  used,
}: {
  toolCall: CoPilotAgentMessageToolCallDebug;
  toolEntries: CoPilotAgentMessageDebugEntry[];
  used: Set<number>;
}): number {
  const directIndex = toolEntries.findIndex(
    (entry) => !used.has(entry.index) && isMatch(entry, toolCall),
  );

  if (directIndex !== -1) {
    return toolEntries[directIndex].index;
  }

  if (toolCall.id) {
    return -1;
  }

  const fallback = toolEntries.find(
    (entry) => !used.has(entry.index) && !entry.toolCallId,
  );

  return fallback ? fallback.index : -1;
}

function buildToolNode({
  toolCall,
  toolEntry,
  idPrefix,
  context,
}: {
  toolCall: CoPilotAgentMessageToolCallDebug;
  toolEntry?: CoPilotAgentMessageDebugEntry;
  idPrefix: string;
  context: TraceBuildContext;
}): AgentTraceNode {
  const outputText = toolEntry ? extractReadableText(toolEntry.content) : '';
  const name = toolEntry?.name ?? toolCall.name ?? 'tool';
  const previewSource = outputText || safeStringify(toolCall.args) || name;
  const resolvedToolCallId = toolEntry?.toolCallId ?? toolCall.id;
  const subAgentRun = resolveSubAgentRun({
    toolName: name,
    toolCallId: resolvedToolCallId,
    context,
  });

  const children = subAgentRun
    ? buildMessageNodes({
        messages: subAgentRun.messages,
        idPrefix: `${idPrefix}sub-${subAgentRun.toolCallId}-`,
        context,
      })
    : [];

  return {
    id: toolEntry
      ? `${idPrefix}msg-${toolEntry.index}`
      : `${idPrefix}call-${toolCall.id ?? name}`,
    kind: subAgentRun ? 'subagent' : 'tool',
    name: subAgentRun ? `${subAgentRun.subAgentType} (sub-agent)` : name,
    preview: toPreview(previewSource),
    status: toolEntry?.status ?? (toolEntry ? undefined : 'pending'),
    skipped: toolEntry?.skippedFromHistory,
    input:
      toolCall.args === undefined
        ? undefined
        : { type: 'json', value: toolCall.args },
    output: toolEntry ? { type: 'text', text: outputText } : undefined,
    metadata: {
      toolCallId: resolvedToolCallId,
      status: toolEntry?.status,
      resolved: Boolean(toolEntry),
      ...(subAgentRun
        ? {
            subAgentType: subAgentRun.subAgentType,
            subAgentRunId: subAgentRun.subAgentRunId,
            subAgentMessageCount: subAgentRun.messageCount,
          }
        : {}),
      ...(toolEntry?.tags && toolEntry.tags.length > 0
        ? { tags: toolEntry.tags }
        : {}),
      ...(toolEntry?.skippedFromHistory ? { skippedFromHistory: true } : {}),
    },
    children,
  };
}

function resolveSubAgentRun({
  toolName,
  toolCallId,
  context,
}: {
  toolName: string;
  toolCallId?: string;
  context: TraceBuildContext;
}): CoPilotSubAgentRunDebug | undefined {
  if (toolName !== TASK_TOOL_NAME || !toolCallId) {
    return undefined;
  }

  if (context.visitedToolCallIds.has(toolCallId)) {
    return undefined;
  }

  const run = context.subAgentRunsByToolCallId.get(toolCallId);

  if (!run) {
    return undefined;
  }

  context.visitedToolCallIds.add(toolCallId);

  return run;
}

function safeStringify(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function buildMessageNode({
  entry,
  toolEntries,
  used,
  idPrefix,
  context,
}: {
  entry: CoPilotAgentMessageDebugEntry;
  toolEntries: CoPilotAgentMessageDebugEntry[];
  used: Set<number>;
  idPrefix: string;
  context: TraceBuildContext;
}): AgentTraceNode {
  const readable = extractReadableText(entry.content);
  const name = nodeName(entry);

  if (entry.messageType === 'ai') {
    const toolCalls = entry.toolCalls ?? [];
    const children = toolCalls.map((toolCall) => {
      const matchedIndex = findToolEntryIndex({ toolCall, toolEntries, used });
      const toolEntry =
        matchedIndex === -1
          ? undefined
          : toolEntries.find((candidate) => candidate.index === matchedIndex);

      if (toolEntry) {
        used.add(toolEntry.index);
      }

      return buildToolNode({ toolCall, toolEntry, idPrefix, context });
    });

    const previewSource =
      readable ||
      (toolCalls.length > 0
        ? toolCalls
            .map((toolCall) => toolCall.name ?? toolCall.id ?? 'tool')
            .join(', ')
        : name);

    return {
      id: `${idPrefix}msg-${entry.index}`,
      kind: 'llm',
      name,
      preview: toPreview(previewSource),
      status: entry.status,
      skipped: entry.skippedFromHistory,
      output: { type: 'llm', text: readable, toolCalls },
      metadata: buildEntryMetadata(entry),
      children,
    };
  }

  const kind: AgentTraceNodeKind =
    entry.messageType === 'human' ? 'human' : 'system';

  return {
    id: `${idPrefix}msg-${entry.index}`,
    kind,
    name,
    preview: toPreview(readable || name),
    status: entry.status,
    skipped: entry.skippedFromHistory,
    input: { type: 'text', text: readable },
    metadata: buildEntryMetadata(entry),
    children: [],
  };
}

function buildMessageNodes({
  messages,
  idPrefix,
  context,
}: {
  messages: CoPilotAgentMessageDebugEntry[];
  idPrefix: string;
  context: TraceBuildContext;
}): AgentTraceNode[] {
  const toolEntries = messages.filter((entry) => entry.messageType === 'tool');
  const used = new Set<number>();
  const nodes: AgentTraceNode[] = [];

  for (const entry of messages) {
    if (entry.messageType === 'tool') {
      continue;
    }

    nodes.push(
      buildMessageNode({ entry, toolEntries, used, idPrefix, context }),
    );
  }

  for (const entry of toolEntries) {
    if (used.has(entry.index)) {
      continue;
    }

    nodes.push(
      buildToolNode({
        toolCall: { id: entry.toolCallId, name: entry.name },
        toolEntry: entry,
        idPrefix,
        context,
      }),
    );
  }

  return nodes;
}

function buildEntryMetadata(
  entry: CoPilotAgentMessageDebugEntry,
): Record<string, unknown> {
  return {
    index: entry.index,
    messageType: entry.messageType,
    ...(entry.id ? { id: entry.id } : {}),
    ...(entry.name ? { name: entry.name } : {}),
    ...(entry.status ? { status: entry.status } : {}),
    ...(entry.contentTruncated ? { contentTruncated: true } : {}),
    ...(entry.skippedFromHistory ? { skippedFromHistory: true } : {}),
    ...(entry.tags && entry.tags.length > 0 ? { tags: entry.tags } : {}),
  };
}

function buildRootNode(
  response: CoPilotAgentMessagesDebugResponse,
): AgentTraceNode {
  return {
    id: 'root',
    kind: 'chain',
    name: 'CoPilot',
    preview: `${response.messageCount} messages`,
    metadata: {
      source: response.source,
      ...(response.invocationSequence
        ? { invocationSequence: response.invocationSequence }
        : {}),
      threadId: response.threadId,
      applicationId: response.applicationId,
      messageCount: response.messageCount,
      capturedAt: response.capturedAt,
      tools: (response.tools ?? []).map((tool) => tool.name),
    },
    children: [],
  };
}

function buildTraceContext(
  response: CoPilotAgentMessagesDebugResponse,
): TraceBuildContext {
  const subAgentRunsByToolCallId = new Map<string, CoPilotSubAgentRunDebug>();

  for (const run of response.subAgentRuns ?? []) {
    if (run.toolCallId) {
      subAgentRunsByToolCallId.set(run.toolCallId, run);
    }
  }

  return { subAgentRunsByToolCallId, visitedToolCallIds: new Set<string>() };
}

function buildTree(
  response: CoPilotAgentMessagesDebugResponse,
): AgentTraceNode {
  const root = buildRootNode(response);
  const context = buildTraceContext(response);

  if (response.systemPrompt?.trim()) {
    root.children.push({
      id: 'system-prompt',
      kind: 'system',
      name: 'System prompt',
      preview: toPreview(response.systemPrompt),
      input: { type: 'text', text: response.systemPrompt },
      metadata: {
        truncated: response.systemPromptTruncated,
        length: response.systemPrompt.length,
      },
      children: [],
    });
  }

  root.children.push(
    ...buildMessageNodes({
      messages: response.messages ?? [],
      idPrefix: '',
      context,
    }),
  );

  return root;
}

function buildFallbackTree(
  response: CoPilotAgentMessagesDebugResponse,
): AgentTraceNode {
  const root = buildRootNode(response);

  for (const entry of response.messages ?? []) {
    const readable = safeStringify(entry.content);
    const kind: AgentTraceNodeKind =
      entry.messageType === 'ai'
        ? 'llm'
        : entry.messageType === 'tool'
          ? 'tool'
          : entry.messageType === 'human'
            ? 'human'
            : 'system';

    root.children.push({
      id: `msg-${entry.index}`,
      kind,
      name: nodeName(entry),
      preview: toPreview(readable || nodeName(entry)),
      status: entry.status,
      input: { type: 'text', text: readable },
      metadata: buildEntryMetadata(entry),
      children: [],
    });
  }

  return root;
}

export function buildAgentTraceTree(
  response: CoPilotAgentMessagesDebugResponse,
): AgentTraceTree {
  try {
    return { root: buildTree(response), degraded: false };
  } catch {
    return { root: buildFallbackTree(response), degraded: true };
  }
}

export function flattenAgentTraceTree(root: AgentTraceNode): AgentTraceNode[] {
  const result: AgentTraceNode[] = [];
  const walk = (node: AgentTraceNode): void => {
    result.push(node);
    for (const child of node.children) {
      walk(child);
    }
  };

  walk(root);

  return result;
}

export function buildParentMap(
  root: AgentTraceNode,
): Map<string, string | null> {
  const parents = new Map<string, string | null>();

  parents.set(root.id, null);
  const walk = (node: AgentTraceNode): void => {
    for (const child of node.children) {
      parents.set(child.id, node.id);
      walk(child);
    }
  };

  walk(root);

  return parents;
}

export function getLastSelectableNodeId(root: AgentTraceNode): string {
  const flat = flattenAgentTraceTree(root);
  const lastNonRoot = flat.filter((node) => node.id !== root.id).at(-1);

  return lastNonRoot?.id ?? root.id;
}
